import React, { useState } from 'react';

type FeedKey = 'olx' | 'imovelweb' | 'chavesnamao';

const FEEDS: Record<FeedKey, { label: string; url: string; description: string }> = {
  olx: {
    label: 'Grupo OLX (OLX, ZAP, Viva Real)',
    url: 'https://ngagtienwooguwquvtbn.supabase.co/functions/v1/xml-feed?format=xml',
    description: 'Padrão VRSync (ListingDataFeed) — usado pelo grupo OLX.',
  },
  imovelweb: {
    label: 'ImovelWeb / Wimoveis',
    url: 'https://ngagtienwooguwquvtbn.supabase.co/functions/v1/xml-imovelweb',
    description: 'Padrão OpenNavent (Grupo Navent) — codigoImobiliaria 47572664.',
  },
  chavesnamao: {
    label: 'Chaves na Mão',
    url: 'https://ngagtienwooguwquvtbn.supabase.co/functions/v1/xml-chavesnamao',
    description: 'Padrão Chaves na Mão (Document/imoveis/imovel) — tags case sensitive em português.',
  },
};

const XmlFeedPage: React.FC = () => {
  const [activeFeed, setActiveFeed] = useState<FeedKey>('olx');
  const [xmlContent, setXmlContent] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const currentFeed = FEEDS[activeFeed];

  const formatXml = (xml: string): string => {
    try {
      const PADDING = '  ';
      const reg = /(>)(<)(\/*)/g;
      let formatted = xml.replace(/\r?\n/g, '').replace(/>\s+</g, '><').replace(reg, '$1\n$2$3');
      let pad = 0;
      return formatted
        .split('\n')
        .map((line) => {
          let indent = 0;
          if (line.match(/^<\/\w/)) pad = Math.max(pad - 1, 0);
          else if (line.match(/^<\w[^>]*[^\/]>.*$/) && !line.match(/<.*<\/.*>/)) indent = 1;
          const result = PADDING.repeat(pad) + line;
          pad += indent;
          return result;
        })
        .join('\n');
    } catch {
      return xml;
    }
  };



  const fetchXMLFeed = async () => {
    setLoading(true);
    setXmlContent('');
    try {
      const response = await fetch(currentFeed.url, { headers: { Accept: 'application/xml' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      setXmlContent(text);
    } catch (error) {
      console.error('Erro ao buscar XML:', error);
      alert('Erro ao buscar XML feed');
    } finally {
      setLoading(false);
    }
  };

  const downloadXML = () => {
    if (!xmlContent) {
      alert('Primeiro carregue o XML clicando em "Carregar XML"');
      return;
    }
    const blob = new Blob([xmlContent], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fox-vendas-${activeFeed}-${new Date().toISOString().split('T')[0]}.xml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyXMLUrl = () => {
    navigator.clipboard.writeText(currentFeed.url);
    alert('URL copiada para a área de transferência!');
  };

  const switchFeed = (key: FeedKey) => {
    setActiveFeed(key);
    setXmlContent('');
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-4">XML Feeds para Portais Imobiliários</h1>

        {/* Abas */}
        <div className="flex gap-2 border-b border-gray-200 mb-4">
          {(Object.keys(FEEDS) as FeedKey[]).map((key) => (
            <button
              key={key}
              onClick={() => switchFeed(key)}
              className={`px-4 py-2 -mb-px border-b-2 font-medium transition-colors ${
                activeFeed === key
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              {FEEDS[key].label}
            </button>
          ))}
        </div>

        <p className="text-sm text-gray-600 mb-4">{currentFeed.description}</p>

        <div className="flex gap-4 mb-6 flex-wrap">
          <button
            onClick={fetchXMLFeed}
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Carregando...' : 'Carregar XML'}
          </button>

          <button
            onClick={downloadXML}
            disabled={!xmlContent}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
          >
            📥 Baixar XML
          </button>

          <button
            onClick={copyXMLUrl}
            className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
          >
            📋 Copiar URL do Feed
          </button>
        </div>

        <div className="bg-gray-100 p-4 rounded mb-4">
          <h3 className="font-semibold mb-2">URL do Feed:</h3>
          <code className="bg-white p-2 rounded block break-all">{currentFeed.url}</code>
        </div>
      </div>

      {xmlContent && (
        <div className="border rounded">
          <div className="bg-gray-50 p-3 border-b">
            <h3 className="font-semibold">Conteúdo XML:</h3>
          </div>
          <pre className="p-4 overflow-auto max-h-[600px] text-xs bg-white whitespace-pre-wrap break-all">{formatXml(xmlContent)}</pre>
        </div>
      )}

      {!xmlContent && !loading && (
        <div className="text-center py-12 text-gray-500">
          Clique em "Carregar XML" para visualizar o feed
        </div>
      )}
    </div>
  );
};

export default XmlFeedPage;
